import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CalendarIcon, Clock, MapPin, Loader2, AlertTriangle, CheckCircle2, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription, DialogHeader,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DURATIONS = [
  { v: 15, l: "15 Min" },
  { v: 30, l: "30 Min" },
  { v: 45, l: "45 Min" },
  { v: 60, l: "1 Stunde" },
  { v: 90, l: "1,5 Stunden" },
  { v: 120, l: "2 Stunden" },
];

const LOCATIONS = ["Telefon", "Vor Ort", "Google Meet", "Microsoft Teams", "Zoom"];

function todayISO() { return new Date().toISOString().slice(0, 10); }

/** Build local "YYYY-MM-DDTHH:MM:00" string respecting browser timezone */
function localISO(dateStr, timeStr) {
  return `${dateStr}T${timeStr}:00`;
}

/** Time-slot grid 08:00 → 19:30 in 30-min steps */
function buildSlots() {
  const out = [];
  for (let h = 8; h <= 19; h++) {
    for (const m of [0, 30]) {
      out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return out;
}
const SLOTS = buildSlots();

function formatTimeRange(startISO, durationMin) {
  const start = new Date(startISO);
  const end = new Date(start.getTime() + durationMin * 60_000);
  const fmt = (d) => `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return `${fmt(start)}–${fmt(end)}`;
}

export default function AppointmentDialog({ open, onClose, lead, onCreated }) {
  const [date, setDate] = useState(todayISO());
  const [time, setTime] = useState("10:00");
  const [duration, setDuration] = useState(30);
  const [title, setTitle] = useState("");
  const [location, setLocation] = useState("Telefon");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [dayAppointments, setDayAppointments] = useState([]);
  const [loadingDay, setLoadingDay] = useState(false);

  // Reset on open
  useEffect(() => {
    if (open && lead) {
      setDate(todayISO());
      setTime("10:00");
      setDuration(30);
      setTitle(`Termin mit ${lead.company_name}`);
      setLocation("Telefon");
      setNotes("");
    }
  }, [open, lead]);

  // Fetch appointments for the selected day
  useEffect(() => {
    if (!open || !date) return;
    let cancelled = false;
    (async () => {
      setLoadingDay(true);
      try {
        const from = `${date}T00:00:00`;
        const to = `${date}T23:59:59`;
        const res = await axios.get(`${API}/appointments`, {
          params: { from_at: from, to_at: to },
          withCredentials: true,
        });
        if (!cancelled) setDayAppointments(res.data?.items || []);
      } catch {
        if (!cancelled) setDayAppointments([]);
      } finally {
        if (!cancelled) setLoadingDay(false);
      }
    })();
    return () => { cancelled = true; };
  }, [date, open]);

  // Determine which slots conflict
  const occupiedRanges = useMemo(() => {
    return dayAppointments.map(a => ({
      start: new Date(a.start_at).getTime(),
      end: new Date(a.end_at).getTime(),
      label: a.title,
      company: a.company_name,
    }));
  }, [dayAppointments]);

  const slotIsBusy = (slot) => {
    const startMs = new Date(localISO(date, slot)).getTime();
    const endMs = startMs + duration * 60_000;
    return occupiedRanges.find(r => startMs < r.end && endMs > r.start);
  };

  const currentConflict = slotIsBusy(time);

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Titel fehlt");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/leads/${lead.id || lead._id}/appointments`,
        {
          title: title.trim(),
          start_at: localISO(date, time),
          duration_min: duration,
          location,
          notes,
        },
        { withCredentials: true }
      );
      toast.success("Termin gebucht · Stage → Qualifiziert");
      onCreated?.(res.data);
      onClose();
    } catch (err) {
      const msg = err.response?.data?.detail || "Fehler beim Speichern";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl rounded-md p-0 overflow-hidden" data-testid="appointment-dialog">
        <DialogHeader className="px-6 py-5 border-b border-border bg-muted/30">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="flex items-center gap-2 text-lg">
                <CalendarIcon size={18} className="text-cyan-500" /> Termin buchen
              </DialogTitle>
              <DialogDescription className="text-xs mt-1">
                {lead?.company_name} {lead?.contact_name && `· ${lead.contact_name}`}
              </DialogDescription>
            </div>
            <button onClick={onClose} className="p-1 rounded-sm text-muted-foreground hover:bg-muted hidden">
              <X size={16}/>
            </button>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-0">
          {/* LEFT: form */}
          <div className="md:col-span-3 p-6 space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Titel</Label>
              <Input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="z.B. Discovery Call"
                className="h-9 mt-1 rounded-sm"
                data-testid="appt-title"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Datum</Label>
                <Input
                  type="date"
                  value={date}
                  onChange={e => setDate(e.target.value)}
                  min={todayISO()}
                  className="h-9 mt-1 rounded-sm"
                  data-testid="appt-date"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Dauer</Label>
                <Select value={String(duration)} onValueChange={v => setDuration(Number(v))}>
                  <SelectTrigger className="h-9 mt-1 rounded-sm" data-testid="appt-duration">
                    <SelectValue/>
                  </SelectTrigger>
                  <SelectContent>
                    {DURATIONS.map(d => <SelectItem key={d.v} value={String(d.v)}>{d.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 block">Uhrzeit</Label>
              <div className="grid grid-cols-6 gap-1.5 max-h-44 overflow-y-auto pr-1">
                {SLOTS.map(s => {
                  const busy = slotIsBusy(s);
                  const selected = time === s;
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setTime(s)}
                      data-testid={`appt-slot-${s}`}
                      className={`text-xs rounded-sm py-1.5 border transition-colors ${
                        selected
                          ? busy
                            ? "bg-red-500/15 border-red-500/40 text-red-500 font-bold"
                            : "bg-foreground text-background border-foreground font-bold"
                          : busy
                            ? "bg-red-500/5 border-red-500/20 text-red-500 line-through hover:bg-red-500/10"
                            : "bg-background border-border text-foreground hover:border-foreground/40"
                      }`}
                      title={busy ? `Belegt: ${busy.label}` : "Frei"}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
              {currentConflict && (
                <div className="mt-2 flex items-start gap-2 p-2.5 bg-red-500/10 border border-red-500/30 rounded-sm text-xs">
                  <AlertTriangle size={13} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <span className="text-red-500">
                    <strong>Konflikt!</strong> Du hast bereits „{currentConflict.label}"
                    {currentConflict.company && <> mit {currentConflict.company}</>} um diese Zeit.
                  </span>
                </div>
              )}
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Ort / Format</Label>
              <Select value={location} onValueChange={setLocation}>
                <SelectTrigger className="h-9 mt-1 rounded-sm" data-testid="appt-location">
                  <SelectValue/>
                </SelectTrigger>
                <SelectContent>
                  {LOCATIONS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Notizen</Label>
              <Textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={3}
                placeholder="Agenda, Vorbereitung, Links …"
                className="mt-1 resize-none rounded-sm text-sm"
                data-testid="appt-notes"
              />
            </div>
          </div>

          {/* RIGHT: day overview */}
          <div className="md:col-span-2 bg-muted/20 border-l border-border p-5 space-y-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Dein Kalender</p>
              <p className="text-sm font-bold mt-0.5">
                {new Date(date).toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long" })}
              </p>
            </div>
            {loadingDay ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 size={12} className="animate-spin"/> Lädt…</div>
            ) : dayAppointments.length === 0 ? (
              <div className="text-xs text-muted-foreground italic flex items-center gap-1.5">
                <CheckCircle2 size={12} className="text-green-500"/> Keine bestehenden Termine
              </div>
            ) : (
              <div className="space-y-2">
                {dayAppointments
                  .sort((a, b) => new Date(a.start_at) - new Date(b.start_at))
                  .map(a => (
                  <div key={a.id} className="bg-card border border-border rounded-sm p-2.5">
                    <div className="flex items-center gap-2 text-xs font-mono">
                      <Clock size={11} className="text-muted-foreground"/>
                      {formatTimeRange(a.start_at, a.duration_min)}
                    </div>
                    <div className="text-xs font-semibold mt-0.5">{a.title}</div>
                    {a.company_name && <div className="text-[10px] text-muted-foreground">{a.company_name}</div>}
                    {a.location && (
                      <div className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5">
                        <MapPin size={9}/> {a.location}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div className="px-6 py-4 border-t border-border bg-card flex items-center justify-between gap-3">
          <div className="text-[11px] text-muted-foreground">
            {!currentConflict && <>Termin: <strong>{formatTimeRange(localISO(date, time), duration)}</strong></>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="h-9 rounded-sm" data-testid="appt-cancel-btn">Abbrechen</Button>
            <Button
              onClick={handleSave}
              disabled={saving || !!currentConflict}
              className="h-9 rounded-sm bg-cyan-500 hover:bg-cyan-400 text-white"
              data-testid="appt-save-btn"
            >
              {saving ? <Loader2 size={14} className="animate-spin mr-2"/> : <CalendarIcon size={14} className="mr-2"/>}
              Termin buchen
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
